import React, { ReactNode } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { X as XIcon } from "react-bootstrap-icons";
import Branding from "../../config/Branding";
import FilterDropdown from "./FilterDropdown";
import SearchableFilterDropdown from "./SearchableFilterDropdown";
import { ANY_VALUE } from "../../constants";

type FilterConditionRowProps = {
    condition: FilterCondition;
    availableFilters: Array<DropdownFilter>;
    customerConfig: CustomerConfig;
    conditionIndex: number;
    onUpdate: (condition: FilterCondition) => void;
    onRemove: () => void;
};

/**
 * A single condition row inside a FilterGroupCard.
 * Renders a topic selector and the appropriate value selector for the chosen topic.
 */
export default class FilterConditionRow extends React.Component<FilterConditionRowProps> {
    private handleTopicChange = (topic: string): void => {
        // Reset values when topic changes
        this.props.onUpdate({ topic, values: [] });
    };

    private handleValueChange = (qsObj: Record<string, string>): void => {
        const { condition } = this.props;
        const val = qsObj[condition.topic] ?? "";
        this.props.onUpdate({ ...condition, values: val !== "" ? [val] : [] });
    };

    /** Find the DropdownFilter definition for the current topic */
    private getFilterForTopic(topic: string): DropdownFilter | undefined {
        return this.props.availableFilters.find(f => f.qsParam === topic);
    }

    public render(): ReactNode {
        const { condition, availableFilters, customerConfig, conditionIndex } = this.props;
        const selectedFilter = this.getFilterForTopic(condition.topic);
        const currentValue = condition.values[0] || "";

        // Sort dropdown options alphabetically (same as existing builder pattern)
        const sortedValues = selectedFilter
            ? ([...( selectedFilter.values || [])] as (string | { id: string; label: string })[]).sort((a, b) => {
                const aLabel = typeof a === "string" ? a : String(a.label);
                const bLabel = typeof b === "string" ? b : String(b.label);
                return aLabel.localeCompare(bLabel, undefined, { sensitivity: "base" });
            })
            : [];

        return (
            <div style={{
                background: "rgba(255,255,255,0.04)",
                borderRadius: 4,
                padding: "8px 10px",
                marginBottom: 6
            }}>
                <Row className="align-items-center" style={{ marginBottom: selectedFilter ? 6 : 0 }}>
                    {/* Topic selector */}
                    <Col sm="5" style={{ paddingRight: 4 }}>
                        <Form.Control
                            as="select"
                            size="sm"
                            value={condition.topic}
                            style={{ color: Branding.white, backgroundColor: "#1a1a1a", borderColor: "#444" }}
                            onChange={(e: any) => this.handleTopicChange(e.target.value)}
                        >
                            <option value="">— Select filter —</option>
                            {availableFilters.map(f => (
                                <option key={f.qsParam} value={f.qsParam}>{f.name}</option>
                            ))}
                        </Form.Control>
                    </Col>

                    {/* Value selector */}
                    <Col sm="6" style={{ paddingLeft: 4, paddingRight: 4 }}>
                        {selectedFilter && selectedFilter.filterType === "searchable" && (
                            <SearchableFilterDropdown
                                key={`searchable-${conditionIndex}-${condition.topic}`}
                                filter={selectedFilter}
                                customerConfig={customerConfig}
                                callback={this.handleValueChange}
                                value={currentValue}
                            />
                        )}
                        {selectedFilter && selectedFilter.filterType !== "searchable" && (
                            <FilterDropdown
                                key={`dropdown-${conditionIndex}-${condition.topic}`}
                                filter={{ ...selectedFilter, values: sortedValues } as DropdownFilter}
                                callback={this.handleValueChange}
                                value={currentValue || (selectedFilter.cardinality === "multiple" ? "" : ANY_VALUE)}
                            />
                        )}
                        {!selectedFilter && (
                            <Form.Control size="sm" disabled placeholder="Select a filter first" style={{ backgroundColor: "#111", borderColor: "#333", color: "#666" }} />
                        )}
                    </Col>

                    {/* Remove button */}
                    <Col sm="1" style={{ paddingLeft: 0, textAlign: "right" }}>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={this.props.onRemove}
                            style={{ padding: "2px 6px", lineHeight: 1 }}
                            title="Remove condition"
                        >
                            <XIcon size={12} />
                        </Button>
                    </Col>
                </Row>
            </div>
        );
    }
}

